import { inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { InvoiceDialogAction } from './invoice-dialog.action';
import { ResetStateDefaults } from '../base-action-class.model';
import { Observable } from 'rxjs/internal/Observable';
import { finalize, from, of, throwError } from 'rxjs';
import { CompoApp } from '../../services/CompoApp';
import { InvoiceModel } from '../../models/invoice/invoice.model';
import { combineLatestWith, delay, map } from 'rxjs/operators';
import { RequestClientService } from '../../services/client/request-client.service';
import { ComponentClassDetailDto } from '../../models/component_class/component-class-detail-dto.model';
import { ObjectDetailModel } from '../../models/component/page/detail/object-detail.model';
import { DocumentModel } from '../../models/document/document.model';
import { InProgressMessageModel } from '../../models/in-progress-message/in-progress-message.model';
import {
  InProgressMessageUtilService
} from '../../services/client/in-progress-message/in-progress-message-util.service';
import { ObjectRequestService } from '../../services/request/object-request.service';

export enum InvoiceDialogMode {
  CREATE = 'CREATE',
  EDIT = 'EDIT'
}

interface InvoiceDialogXsStateModel {
  invoiceId: string;
  invoice: InvoiceModel;
  updatedInvoice: InvoiceModel;
  attachments: DocumentModel[];
  preselectedObjectId: string;
  preselectedObject: ObjectDetailModel;
  preselectedClassId: string;
  preselectedClass: ComponentClassDetailDto;
  invoiceOrganizationId: string;
  active: boolean;
  initialized: boolean;
  mode: InvoiceDialogMode;
  inProgress: boolean;
}

const getDefaults = (): InvoiceDialogXsStateModel => ({
  invoiceId: null,
  invoice: null,
  updatedInvoice: null,
  attachments: [],
  preselectedObjectId: null,
  preselectedObject: null,
  preselectedClassId: null,
  preselectedClass: null,
  invoiceOrganizationId: null,
  active: false,
  initialized: false,
  mode: null,
  inProgress: false,
});

@State<InvoiceDialogXsStateModel>({
  name: 'invoiceDialog',
  defaults: getDefaults()
})
@Injectable()
export class InvoiceDialogXsState {

  readonly #objectRequestService = inject(ObjectRequestService);

  //region CONSTRUCTOR
  constructor(
    private app: CompoApp,
    private requestService: RequestClientService
  ) {
  }
  //endregion

  //region SELECTORS
  @Selector()
  public static invoice(state: InvoiceDialogXsStateModel): InvoiceModel {
    return !!state.invoice ? {...state.invoice} : null;
  }

  @Selector()
  public static attachments(state: InvoiceDialogXsStateModel): DocumentModel[] {
    return state.attachments;
  }

  @Selector()
  public static invoiceOrganizationId(state: InvoiceDialogXsStateModel): string {
    return state.invoiceOrganizationId;
  }

  @Selector()
  public static preselectedObjectId(state: InvoiceDialogXsStateModel): string {
    return state.preselectedObjectId;
  }

  @Selector()
  public static preselectedObject(state: InvoiceDialogXsStateModel): ObjectDetailModel {
    return state.preselectedObject;
  }

  @Selector()
  public static preselectedClassId(state: InvoiceDialogXsStateModel): string {
    return state.preselectedClassId;
  }

  @Selector()
  public static preselectedClass(state: InvoiceDialogXsStateModel): ComponentClassDetailDto {
    return state.preselectedClass;
  }

  @Selector()
  public static active(state: InvoiceDialogXsStateModel): boolean {
    return state.active;
  }

  @Selector()
  public static initialized(state: InvoiceDialogXsStateModel): boolean {
    return state.initialized;
  }

  @Selector()
  public static mode(state: InvoiceDialogXsStateModel): InvoiceDialogMode {
    return state.mode;
  }

  @Selector()
  public static inProgress(state: InvoiceDialogXsStateModel): boolean {
    return state.inProgress;
  }

  @Selector()
  public static exactlyOneDocumentStored(state: InvoiceDialogXsStateModel): boolean {
    return state.attachments.length === 1;
  }
  //endregion

  //region ACTIONS
  @Action(InvoiceDialogAction.Create)
  public create(
    ctx: StateContext<InvoiceDialogXsStateModel>,
    action: InvoiceDialogAction.Create
  ): Observable<void> {
    const state: InvoiceDialogXsStateModel = ctx.getState();

    if (state.active) {
      throw new Error('State is already active!');
    }

    if (state.inProgress) {
      throw new Error('Request is in progress!');
    }

    ctx.patchState({
      ...getDefaults(),
      invoiceId: null,
      invoiceOrganizationId: this.app.data.getUser()?.organization?.id,
      active: true,
      mode: InvoiceDialogMode.CREATE,
      inProgress: true
    });

    const requestStartDate: Date = new Date();
    const preselectedRequest: Observable<void> = this.fetchPreselected(
      action.preselectedObjectId,
      action.preselectedComponentClassId,
      ctx
    );

    return preselectedRequest
      .pipe(
        delay(this.requestService.calcDefaultTimeoutOf(requestStartDate)),
        map(() => {
          ctx.patchState({
            invoice: null,
            updatedInvoice: null,
            attachments: [],
            initialized: true
          });
        }),
        finalize(() => {
          ctx.patchState({
            inProgress: false
          });
        })
      );
  }

  @Action(InvoiceDialogAction.Edit)
  public edit(
    ctx: StateContext<InvoiceDialogXsStateModel>,
    action: InvoiceDialogAction.Edit
  ): Observable<void> {
    const state: InvoiceDialogXsStateModel = ctx.getState();

    if (state.active) {
      return throwError(() => new Error('State is already active!'));
    }

    if (state.inProgress) {
      return throwError(() => new Error('Request is in progress!'));
    }

    if (!action.invoiceId) {
      return throwError(() => new Error('Invoice id cannot be null!'));
    }

    ctx.patchState({
      ...getDefaults(),
      invoiceId: action.invoiceId,
      active: true,
      mode: InvoiceDialogMode.EDIT,
      inProgress: true
    });

    const requestStartDate: Date = new Date();

    const preselectedRequest: Observable<void> = this.fetchPreselected(
      action.preselectedObjectId,
      action.preselectedComponentClassId,
      ctx
    );

    return this.app.invoiceService.getById(action.invoiceId)
      .pipe(
        combineLatestWith(preselectedRequest),
        delay(this.requestService.calcDefaultTimeoutOf(requestStartDate)),
        map(([invoice, _]: [InvoiceModel, any]) => {
          ctx.patchState({
            invoice,
            updatedInvoice: invoice,
            attachments: invoice.documents,
            invoiceOrganizationId: invoice.organizationId,
            initialized: true
          });
        }),
        finalize(() => {
          ctx.patchState({
            inProgress: false
          });
        })
      );
  }

  @Action(InvoiceDialogAction.AddAttachment)
  public addAttachment(
    ctx: StateContext<InvoiceDialogXsStateModel>,
    action: InvoiceDialogAction.AddAttachment
  ): void {
    const state: InvoiceDialogXsStateModel = ctx.getState();

    if (!state.active || !state.initialized) {
      throw new Error('Dialog is not initialized!');
    }

    if (state.inProgress) {
      throw new Error('Request is in progress!');
    }

    if (!action.document) {
      throw new Error('Document cannot be null!');
    }

    if (!action.document.documentDataId) {
      throw new Error('Document data id cannot be null!');
    }

    ctx.patchState({
      attachments: [
        ...state.attachments,
        action.document
      ]
    });
  }

  @Action(InvoiceDialogAction.DeleteAttachment)
  public deleteAttachment(
    ctx: StateContext<InvoiceDialogXsStateModel>,
    action: InvoiceDialogAction.DeleteAttachment
  ): void {
    const state: InvoiceDialogXsStateModel = ctx.getState();

    if (!state.active || !state.initialized) {
      throw new Error('Dialog is not initialized!');
    }

    if (state.inProgress) {
      throw new Error('Request is in progress!');
    }

    if (!action.documentDataId) {
      throw new Error('Document data id cannot be null!');
    }

    ctx.patchState({
      attachments: state.attachments.filter(document => document.documentDataId !== action.documentDataId)
    });
  }

  @Action(InvoiceDialogAction.UpdateInvoice)
  public updateInvoice(
    ctx: StateContext<InvoiceDialogXsStateModel>,
    action: InvoiceDialogAction.UpdateInvoice
  ): void {
    const state: InvoiceDialogXsStateModel = ctx.getState();

    if (!state.active || !state.initialized) {
      throw new Error('Dialog is not initialized!');
    }

    if (state.inProgress) {
      throw new Error('Request is in progress!');
    }

    if (!action.invoice) {
      throw new Error('Invoice cannot be null!');
    }

    ctx.patchState({
      updatedInvoice: {
        ...(state.invoice || {}),
        ...action.invoice
      }
    });
  }

  @Action(InvoiceDialogAction.Save)
  public save(
    ctx: StateContext<InvoiceDialogXsStateModel>
  ): Observable<void> {
    const state: InvoiceDialogXsStateModel = ctx.getState();

    if (!state.active || !state.initialized) {
      return throwError(() => new Error('Dialog is not initialized!'));
    }

    if (state.inProgress) {
      return throwError(() => new Error('Request is in progress!'));
    }

    if (!state.updatedInvoice) {
      return throwError(() => new Error('Invoice is null!'));
    }

    const invoice: InvoiceModel = {
      ...state.updatedInvoice,
      id: state.invoiceId,
      documents: state.attachments,
      organizationId: state.invoiceOrganizationId,
      signed: true,
      aiGenerated: state.invoice?.aiGenerated
    };

    ctx.patchState({
      inProgress: true
    });

    const requestStartDate: Date = new Date();

    let request: Observable<any> = of();

    switch (state.mode) {
      case InvoiceDialogMode.CREATE: {
        request = from(this.app.invoiceService.create(invoice));
        break;
      }
      case InvoiceDialogMode.EDIT: {
        request = from(this.app.invoiceService.update(state.invoiceId, invoice));
        break;
      }
    }

    return request
      .pipe(
        delay(this.requestService.calcDefaultTimeoutOf(requestStartDate)),
        map(() => {
          ctx.setState(getDefaults());
        }),
        finalize(() => {
          ctx.patchState({
            inProgress: false
          });
        })
      );
  }

  @Action(InvoiceDialogAction.Delete)
  public delete(
    ctx: StateContext<InvoiceDialogXsStateModel>
  ): Observable<void> {
    const state: InvoiceDialogXsStateModel = ctx.getState();

    if (!state.active || !state.initialized) {
      return throwError(() => new Error('Dialog is not initialized!'));
    }

    if (state.inProgress) {
      return throwError(() => new Error('Request is in progress!'));
    }

    if (!state.invoiceId) {
      return throwError(() => new Error('Invoice id is null!'));
    }

    ctx.patchState({
      inProgress: true
    });

    const requestStartDate: Date = new Date();

    return from(this.app.invoiceService.delete(state.invoiceId))
      .pipe(
        delay(this.requestService.calcDefaultTimeoutOf(requestStartDate)),
        map(() => {
          ctx.setState(getDefaults());
        }),
        finalize(() => {
          ctx.patchState({
            inProgress: false
          });
        })
      );
  }

  @Action(InvoiceDialogAction.ExtractPositionWithAI)
  public extractPositionWithAi(
    ctx: StateContext<InvoiceDialogXsStateModel>,
    action: InvoiceDialogAction.ExtractPositionWithAI): Observable<void> {
    const state = ctx.getState();
    const componentId = state.preselectedObjectId;
    const documents = state.attachments;

    if (documents.length !== 1) {
      return;
    }

    return from(this.app.invoiceService.extractPositionWithAI(componentId, documents[0].documentDataId))
      .pipe(
        map(demandId => {
          const message: InProgressMessageModel
            = InProgressMessageUtilService.extractingInvoicePositionsWithAI(demandId, false);
          this.app.inProgressMessageService.addNewMessage(message);
        }));
  }

  @Action([
    InvoiceDialogAction.ResetState,
    ResetStateDefaults
  ])
  public resetState(ctx: StateContext<InvoiceDialogXsStateModel>): void {
    ctx.setState(getDefaults());
  }
  //endregion

  //region HELPERS
  private fetchPreselected(
    preselectedObjectId: string,
    preselectedComponentClassId: string,
    ctx: StateContext<InvoiceDialogXsStateModel>
  ): Observable<void> {
    if (preselectedObjectId) {
      return from(this.#objectRequestService.byId(preselectedObjectId))
        .pipe(
          map((object: ObjectDetailModel) => {
            ctx.patchState({
              preselectedObjectId: preselectedObjectId,
              preselectedObject: object
            });
          })
        );
    } else if (preselectedComponentClassId) {
      return from(this.app.classService.getById(preselectedComponentClassId))
        .pipe(
          map((componentClass: ComponentClassDetailDto) => {
            ctx.patchState({
              preselectedClassId: preselectedComponentClassId,
              preselectedClass: componentClass
            });
          })
        );
    } else {
      return of(null);
    }
  }
  //endregion

}
